import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { onChangeTitleDebounceTime } from '@confluence/content-types-utils/entry-points/contentTypeConstants';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { containsNetworkError } from '@confluence/network';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';

import { usePublishModalAnalyticsEvent } from './usePublishModalAnalyticsEvent';
import { ValidatePagePublishQuery } from './ValidatePagePublishQuery.graphql';
import { displayPublishErrorFlag } from './publishHelpers';

type UsePrePublishTitleValidationArgs = {
	contentId: string | undefined;
	contentType: string;
	isUnpublishedDraft: boolean;
	flags: FlagsStateContainer;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	onBeforeValidation: () => void;
	onValidationSuccess: () => void;
	analyticsSource: string;
	optimisticTitle?: string | null;
};

export const usePrePublishTitleValidation = ({
	contentId,
	contentType,
	isUnpublishedDraft,
	flags,
	retrieveEditorTitleAndContent,
	onBeforeValidation,
	onValidationSuccess,
	analyticsSource,
	optimisticTitle,
}: UsePrePublishTitleValidationArgs) => {
	const intl = useIntl();

	// Since the validatePageAndOpenModal function may be running after this component has unmounted,
	// we have to query the apolloClient directly rather than using useQuery's refetch function.
	const apolloClient = getApolloClient();

	// Changes to EditorTitle are debounced if the page is a draft, which we need to account for
	const titleDebounceTime = isUnpublishedDraft ? onChangeTitleDebounceTime : 0;

	const { createAnalyticsEvent } = usePublishModalAnalyticsEvent();

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const isLivePage = useIsCurrentPageLive();

	const validatePageAndOpenModal = useCallback(
		(actionSubjectId: string) => {
			const validatePage = async () => {
				// Live Pages only save changes to the `realTitle` when it is valid,
				// so we need to use the `optimisticTitle` here to get what the user
				// has actually typed into the title field, and then show the correct flag.
				const { title: realTitle } = await retrieveEditorTitleAndContent();
				// ?? must be used here to explicitly check null and undefined because we want to use optmisticTitle if it is an empty string, which is also falsey.
				const title = optimisticTitle ?? realTitle;
				try {
					// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
					const { data } = await apolloClient.query({
						query: ValidatePagePublishQuery,
						errorPolicy: 'all',
						fetchPolicy: 'network-only',
						variables: {
							contentId,
							type: contentType,
							title,
						},
					});
					// This is the same logic used to calculate `isTitleEmpty` in EditorPrimaryToolbarComponents.
					const isTitleEmpty = !title;
					if (!data.validatePagePublish?.isValid) {
						await displayPublishErrorFlag({
							validationMessage: data.validatePagePublish?.message,
							isTitleEmpty,
							flags,
							formatMessage: intl.formatMessage,
							publish: validatePageAndOpenModal('PublishButtonInFlag'),
							isAIEnabledForContent,
							isLivePage,
						});
					} else {
						onValidationSuccess();
					}
				} catch (error) {
					if (error) {
						markErrorAsHandled(error);
						if (!containsNetworkError(error) && !isUnauthorizedError(error)) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.PAGE_MODES,
							});
						}
					}
				}
			};
			return async () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'button',
						action: 'clicked',
						actionSubjectId,
						source: analyticsSource,
						attributes: {
							isUnpublishedDraft,
						},
					},
				}).fire();
				setTimeout(async () => {
					onBeforeValidation();
					await validatePage();
				}, titleDebounceTime);
			};
		},
		[
			contentId,
			contentType,
			retrieveEditorTitleAndContent,
			createAnalyticsEvent,
			flags,
			intl.formatMessage,
			titleDebounceTime,
			isUnpublishedDraft,
			analyticsSource,
			onBeforeValidation,
			onValidationSuccess,
			apolloClient,
			optimisticTitle,
			isAIEnabledForContent,
			isLivePage,
		],
	);

	return validatePageAndOpenModal;
};
